import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Outlet } from "react-router-dom";
import Sidebar from "Components/Sidebar";
import { CSSProperties } from "@mui/material/styles/createTypography";
import {
  Checkbox,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ViewContext } from "Contexts/ViewProvider";
import { DataContext } from "Contexts/DataProvider";
import { useThemeMode } from "Contexts/ThemeContext";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { PiMapPinSimpleArea } from "react-icons/pi";
import { AiOutlineFieldNumber } from "react-icons/ai";
import { FaSortNumericDown } from "react-icons/fa";
import { HiBellAlert } from "react-icons/hi2";

const drawerWidth = 240;

// 基本タブのstyle
export const baseLinkStyle: CSSProperties = {
  textDecoration: "none",
  color: "inherit",
  display: "block",
};

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);

  // サイドバーデータ持ち回り
  const sidebarContext = React.useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { choiceMenu } = sidebarContext;

  // ダークモード
  const { mode, toggleMode } = useThemeMode();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Drawerが閉じられたときに発火
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  // SIMID,磁界IDスイッチ
  const context = React.useContext(ViewContext);

  if (!context) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const {
    isName,
    setIsName,
    visible,
    isAlertPrioritized,
    setIsAlertPrioritized,
    isAlertPriorityVisible,
  } = context;

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsName(event.target.checked);
  };

  const handleAlertPriorityChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsAlertPrioritized(event.target.checked);
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
      }}
    >
      <CssBaseline />
      {/* ヘッダー */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          background: theme.palette.custom.header,
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Typography variant="h6" noWrap>
              {choiceMenu}
            </Typography>
            {!isXs && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={mode === "dark"}
                    onChange={toggleMode}
                    sx={{
                      // 通常時のアイコン色
                      color: "rgba(255, 255, 255, 0.7)",
                      // チェック時のアイコン色
                      "&.Mui-checked": {
                        color: "#ffffff",
                      },
                    }}
                  />
                }
                label={<Typography noWrap>ダークモード</Typography>}
              />
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              ml: "auto",
              gap: 1.5,
            }}
          >
            {visible &&
              (isXs ? (
                <>
                  {isAlertPriorityVisible && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Typography
                        sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                        variant="caption"
                      >
                        並び順
                      </Typography>
                      <Checkbox
                        sx={{ padding: 0, margin: 0 }}
                        checked={isAlertPrioritized}
                        onChange={handleAlertPriorityChange}
                        icon={
                          <FaSortNumericDown
                            size={25}
                            style={{
                              color: "white",
                            }}
                          />
                        }
                        checkedIcon={
                          <HiBellAlert
                            size={25}
                            style={{
                              color: "white",
                            }}
                          />
                        }
                      />
                    </Box>
                  )}
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Typography
                      sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
                      variant="caption"
                    >
                      表示切替
                    </Typography>
                    <Checkbox
                      sx={{ padding: 0, margin: 0 }}
                      checked={isName}
                      onChange={handleSwitchChange}
                      icon={
                        <AiOutlineFieldNumber
                          size={25}
                          style={{
                            color: "white",
                          }}
                        />
                      }
                      checkedIcon={
                        <PiMapPinSimpleArea
                          size={25}
                          style={{
                            color: "white",
                          }}
                        />
                      }
                    />
                  </Box>
                </>
              ) : (
                <Box>
                  {isAlertPriorityVisible && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isAlertPrioritized}
                          onChange={handleAlertPriorityChange}
                          sx={{
                            // 通常時のアイコン色
                            color: "rgba(255, 255, 255, 0.7)",
                            // チェック時のアイコン色
                            "&.Mui-checked": {
                              color: "#ffffff",
                            },
                          }}
                        />
                      }
                      label={<Typography noWrap>アラート優先表示</Typography>}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size={"small"}
                        checked={isName}
                        onChange={handleSwitchChange}
                      />
                    }
                    label={isName ? "エリア名称" : "シリアルNo"}
                    sx={{ ml: "20px" }}
                  />
                </Box>
              ))}

            {isXs && (
              <Box display="flex" flexDirection="column" alignItems="center">
                <Typography
                  sx={{ fontSize: "0.8rem", lineHeight: 1.5, mb: 0, pb: 0 }}
                  variant="caption"
                >
                  モード切替
                </Typography>
                <Checkbox
                  sx={{ padding: 0, margin: 0 }}
                  checked={mode === "dark"}
                  onChange={toggleMode}
                  icon={<LightModeIcon style={{ color: "white" }} />}
                  checkedIcon={<DarkModeIcon style={{ color: "white" }} />}
                />
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      {/* サイドバー */}
      <Sidebar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        handleDrawerTransitionEnd={handleDrawerTransitionEnd}
        handleDrawerClose={handleDrawerClose}
        handleDrawerToggle={handleDrawerToggle}
      />
      {/* メインコンテンツ */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 2,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: theme.palette.custom.page,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
